// purpose: 大型撃破・ボス・自機死亡の瞬間に広がる加算リング。固定 3 スロット・毎フレーム再描画・定常割当ゼロ。

import type { Graphics } from 'pixi.js';
import type { MobileImpactPrimitives } from './MobileImpactPrimitives';

/**
 * An expanding ring is the cheapest way to say "that was big" without a filter:
 * one stroked circle per live event, redrawn into a Graphics the caller clears
 * every frame. No render target, no texture, no pool of sprites.
 *
 * Three slots, hard cap. A wave wiping out at once must not stack rings into a
 * white disc, and past three the expansions overlap faster than the eye
 * separates them anyway. The cursor overwrites the oldest slot rather than
 * dropping the new event: the newest impact is always the one being read.
 *
 * Alpha moves in discrete steps rather than as a ramp, matching PixelBurst — a
 * smooth fade reads as motion blur against native pixel art. Colour and width
 * are fixed together with alpha in a frozen table, so drawing a ring passes a
 * module const to `stroke()` and allocates nothing.
 */
const RING_SLOTS = 3;
const RING_LIFE_MS = 280;
const RING_RADIUS_START_PX = 8;
const RING_RADIUS_END_PX = 90;

/** FX cyan, cited from fx/PixelBurst.ts SPARK_COLORS; deaths get a warm white so they never read as another kill. */
export const RING_TONE_IMPACT = 0;
export const RING_TONE_DEATH = 1;

interface RingStroke {
  readonly color: number;
  readonly alpha: number;
  readonly width: number;
}

/**
 * Indexed [tone][step]. The step count is the ring's whole life, so its length
 * is the ageing divisor — which means every tone row must stay the same length:
 * update() derives RING_STEPS from row 0 and applies it to all of them.
 */
const RING_STROKES: readonly (readonly RingStroke[])[] = Object.freeze([
  Object.freeze([
    { color: 0x83f5ff, alpha: 0.6, width: 2 },
    { color: 0x83f5ff, alpha: 0.35, width: 2 },
    { color: 0x5cd0f1, alpha: 0.15, width: 1 },
  ]),
  Object.freeze([
    { color: 0xfff2dd, alpha: 0.6, width: 2 },
    { color: 0xffd9a8, alpha: 0.35, width: 2 },
    { color: 0xffd9a8, alpha: 0.15, width: 1 },
  ]),
]);
const RING_STEPS = RING_STROKES[0]!.length;

export class ShockRings {
  private readonly x = new Float32Array(RING_SLOTS);
  private readonly y = new Float32Array(RING_SLOTS);
  private readonly ageMs = new Float32Array(RING_SLOTS);
  private readonly tone = new Uint8Array(RING_SLOTS);
  private readonly step = new Uint8Array(RING_SLOTS);
  private readonly live = new Uint8Array(RING_SLOTS);
  private cursor = 0;

  spawn(x: number, y: number, tone: number): void {
    const slot = this.cursor;
    this.cursor = (slot + 1) % RING_SLOTS;
    this.x[slot] = x;
    this.y[slot] = y;
    this.ageMs[slot] = 0;
    this.tone[slot] = tone;
    this.step[slot] = 0;
    this.live[slot] = 1;
  }

  /** The delta is already timebase-scaled by the caller, so a hit stop freezes the expansion too. */
  update(deltaMs: number): void {
    for (let i = 0; i < RING_SLOTS; i += 1) {
      if (this.live[i] === 0) continue;
      const age = this.ageMs[i]! + deltaMs;
      if (age >= RING_LIFE_MS) {
        this.live[i] = 0;
        continue;
      }
      this.ageMs[i] = age;
      this.step[i] = Math.min(RING_STEPS - 1, Math.floor((age / RING_LIFE_MS) * RING_STEPS));
    }
  }

  /**
   * One `stroke()` per live ring — at most three calls, and the tone table means
   * the style argument is a module const rather than a fresh literal. The radius
   * is rounded because a 1-2px stroke at a fractional radius is what makes a
   * ring shimmer instead of expand.
   */
  draw(rings: Graphics): void {
    for (let i = 0; i < RING_SLOTS; i += 1) {
      if (this.live[i] === 0) continue;
      const progress = this.ageMs[i]! / RING_LIFE_MS;
      const radius = RING_RADIUS_START_PX + (RING_RADIUS_END_PX - RING_RADIUS_START_PX) * progress;
      rings.circle(Math.round(this.x[i]!), Math.round(this.y[i]!), Math.round(radius));
      rings.stroke(RING_STROKES[this.tone[i]!]![this.step[i]!]!);
    }
  }

  /** Mobile swaps immutable Pixi circle contexts instead of rebuilding paths. */
  drawMobile(rings: MobileImpactPrimitives): void {
    for (let i = 0; i < RING_SLOTS; i += 1) {
      if (this.live[i] === 0) continue;
      const progress = this.ageMs[i]! / RING_LIFE_MS;
      const radius = RING_RADIUS_START_PX + (RING_RADIUS_END_PX - RING_RADIUS_START_PX) * progress;
      const stroke = RING_STROKES[this.tone[i]!]![this.step[i]!]!;
      rings.setShockRing(
        i,
        this.x[i]!,
        this.y[i]!,
        radius,
        stroke.color,
        stroke.alpha,
        stroke.width,
      );
    }
  }

  reset(): void {
    this.live.fill(0);
    this.cursor = 0;
  }
}
